import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { featuredProjects } from '../../mocks/projects';
import { clients } from '../../mocks/clients';

export default function HomePage() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const stats = [
    { value: '30+', label: 'Years of Experience' },
    { value: '500+', label: 'Projects Completed' },
    { value: '100+', label: 'Luxury Clients' },
    { value: '5', label: 'States Served' },
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* Hero */}
      <section className="relative h-screen w-full overflow-hidden">
        <div
          className="absolute inset-0 w-full h-full"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        >
          <img
            src="https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/fdb275be03df5e8424988882acc1ea2f.jpeg"
            alt="Reiken Construction Management"
            className="w-full h-full object-cover object-center scale-110"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        <div className="relative z-10 h-full flex flex-col justify-center px-6 sm:px-8 md:px-20 lg:px-28 w-full">
          <div className="max-w-3xl">
            <p className="text-white/70 text-xs sm:text-sm uppercase tracking-[0.3em] mb-4 sm:mb-6 font-light">
              Est. 1995 — New York City
            </p>
            <h1 className="text-white text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold leading-[1.05] mb-6 sm:mb-8">
              Building<br />
              <span className="italic font-light">Excellence</span><br />
              Since 1995
            </h1>
            <p className="text-white/80 text-base sm:text-lg md:text-xl leading-relaxed mb-8 sm:mb-12 max-w-xl font-light">
              New York's premier construction management firm for luxury retail, commercial, and government projects.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/portfolio"
                className="inline-flex items-center justify-center gap-3 bg-white text-black px-8 sm:px-10 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-400 cursor-pointer whitespace-nowrap"
              >
                View Our Work
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center gap-3 border border-white text-white px-8 sm:px-10 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white hover:text-black transition-all duration-400 cursor-pointer whitespace-nowrap"
              >
                Start a Project
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
          <span className="text-white/50 text-xs uppercase tracking-widest">Scroll</span>
          <div className="w-px h-12 bg-white/30 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1/2 bg-white animate-bounce" />
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-black text-white py-10 sm:py-12 px-6 sm:px-8 md:px-20">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 md:gap-0 md:divide-x md:divide-white/20">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center md:px-8">
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2">{stat.value}</p>
              <p className="text-white/60 text-xs sm:text-sm uppercase tracking-widest font-light">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-20 sm:py-28 px-6 sm:px-8 md:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 sm:gap-20 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-black/50 mb-5">What We Do</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-6 sm:mb-8">
                Full-Service Construction<br />
                <span className="italic font-light">Management</span>
              </h2>
              <p className="text-black/70 text-base sm:text-lg leading-relaxed mb-8 sm:mb-10 font-light">
                From initial planning through final occupancy, Reiken manages every detail of your project with precision, expertise, and an unwavering commitment to quality.
              </p>
              <Link
                to="/about-us"
                className="inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-widest border-b-2 border-black pb-1 hover:opacity-60 transition-opacity cursor-pointer whitespace-nowrap"
              >
                Learn About Reiken
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 gap-0 border border-black/10">
              {[
                {
                  icon: 'ri-building-2-line',
                  title: 'General Construction',
                  desc: 'Full-service construction for luxury retail, commercial, and government spaces throughout the tri-state area.',
                },
                {
                  icon: 'ri-tools-line',
                  title: 'Construction Management',
                  desc: 'Comprehensive oversight from planning through completion — on schedule, on budget, every time.',
                },
                {
                  icon: 'ri-store-2-line',
                  title: 'Shop-in-Shop Installs',
                  desc: 'Specialists in luxury brand shop-in-shop installations for the region\'s leading department stores.',
                },
                {
                  icon: 'ri-government-line',
                  title: 'Government Projects',
                  desc: 'Trusted by government agencies for complex, high-compliance construction and renovation projects.',
                },
              ].map((service, i) => (
                <div
                  key={service.title}
                  className={`flex gap-4 sm:gap-6 p-6 sm:p-8 hover:bg-black hover:text-white transition-all duration-300 group cursor-default ${i < 3 ? 'border-b border-black/10' : ''}`}
                >
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 mt-1">
                    <i className={`${service.icon} text-2xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold mb-2">{service.title}</h3>
                    <p className="text-sm leading-relaxed opacity-70 font-light">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 sm:py-28 px-6 sm:px-8 md:px-20 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12 sm:mb-16">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-black/50 mb-5">Our Work</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
                Selected<br />
                <span className="italic font-light">Projects</span>
              </h2>
            </div>
            <Link
              to="/portfolio"
              className="hidden md:inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-widest border-b-2 border-black pb-1 hover:opacity-60 transition-opacity cursor-pointer whitespace-nowrap"
            >
              All Projects
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            {featuredProjects.slice(0, 5).map((project, index) => (
              <Link
                key={project.id}
                to={`/portfolio/${project.slug}`}
                className={`block group relative overflow-hidden cursor-pointer ${
                  index === 0
                    ? 'md:col-span-2 aspect-[4/3] md:aspect-video'
                    : index < 3
                    ? 'aspect-[4/3] md:aspect-[3/4]'
                    : 'aspect-[4/3] md:aspect-video'
                }`}
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-400" />
                <div className="absolute inset-0 flex items-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                  <div>
                    <p className="text-white text-xs uppercase tracking-widest mb-1 font-light">View Project</p>
                    <h3 className="text-white text-xl font-bold">{project.title}</h3>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/60 to-transparent group-hover:opacity-0 transition-opacity duration-400">
                  <h3 className="text-white text-base font-medium">{project.title}</h3>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10 md:hidden">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-widest border-b-2 border-black pb-1 hover:opacity-60 transition-opacity cursor-pointer whitespace-nowrap"
            >
              View All Projects
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Clients */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 md:px-20 border-t border-black/10">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-black/50 text-center mb-10 sm:mb-14">Trusted By The World's Leading Brands</p>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-6 sm:gap-10 items-center">
            {clients.map((client) => (
              <div
                key={client.name}
                className="flex items-center justify-center grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
              >
                <img
                  src={client.logo}
                  alt={client.name}
                  className="w-full h-auto max-w-[100px] sm:max-w-[140px] object-contain"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 sm:py-32 px-6 sm:px-8 md:px-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/dd9ac60da2db526df68eccc344d22237.jpg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/75" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center text-white">
          <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-6">Ready to Build?</p>
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-6 sm:mb-8">
            Let's Create Something<br />
            <span className="italic font-light">Extraordinary</span>
          </h2>
          <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-10 sm:mb-12 font-light max-w-xl mx-auto">
            Contact us today to discuss your project. We bring 30 years of expertise to every build.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <Link
              to="/contact-us"
              className="inline-flex items-center justify-center gap-3 bg-white text-black px-10 sm:px-12 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-transparent hover:text-white border-2 border-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Start a Conversation
            </Link>
            <a
              href="tel:9174320310"
              className="inline-flex items-center justify-center gap-3 border border-white/50 text-white px-8 sm:px-10 py-4 text-sm font-semibold uppercase tracking-widest hover:border-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              917-432-0310
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
